import {ThunkAction, ThunkDispatch} from 'redux-thunk';
import {IStore} from 'redux/interface';
import {Action} from 'redux';
import {Auth0Client} from '@auth0/auth0-spa-js';
import {IUser} from './interfaces';
import {IUserProfile} from '@wholesalechange/chatcomponent';
import {
  clearUserAction, finishAuthorizationAction, loginErrorActionClear,
  loginErrorActionSet,
  setUserAction,
  setUserLanguageAction, setUserNicknameAction
} from './actions';
import {setAxiosHeaders} from 'redux/api/helpers';
import {getUserProfileApi, updateUserProfileApi, updateUserProfileNicknameApi} from 'redux/api/user';
import {loadMediaAction} from 'redux/media/mediaAsyncActions';
import {errorHandler} from 'redux/errorHandler';
import {changeLocale} from '../translation/translationAsyncActions';
import {ToastKey} from 'components/gritx-toast/ToastKeyEnum';
import {finishLoadingAction, startLoadingAction} from '../status/actions';
import {getUserData} from '../pageAsyncActions';

const loginErrorHandler = async ({
  dispatch,
  auth0Client
}: { dispatch: ThunkDispatch<IStore, unknown, Action>, auth0Client: Auth0Client }) => {
  try {
    await auth0Client.logout({
      logoutParams: {
        returnTo: document.location.origin
      }
    });
  } catch (e) {
    console.error('loginErrorHandler error', {
      message: e.message,
      error: e.error,
      errorDescription: e.error_description,
      stack: e.stack
    });
  }
  dispatch(loginErrorActionSet());
};

export const startLoginAction = (appRedirectUri?: string): ThunkAction<void, IStore, unknown, Action> => async (dispatch, getState) => {
  const {auth: {auth0Client} } = getState();

  dispatch(loginErrorActionClear());
  try {
    let queryString = '';

    if (appRedirectUri) {
      const searchParams = new URLSearchParams();

      searchParams.append('appRedirectUri', appRedirectUri);
      queryString = searchParams.toString();
    }
    await auth0Client.loginWithRedirect({
      authorizationParams: {
        prompt: 'login',
        redirect_uri: `${window.location.origin}/callback${queryString && `?${queryString}`}`
      }
    });
  } catch (e) {
    console.error('startLogin error', {
      message: e.message,
      error: e.error,
      errorDescription: e.error_description,
      stack: e.stack
    });
    await loginErrorHandler({
      dispatch,
      auth0Client
    });
  }
};

export const getUserDataAction = (): ThunkAction<void, IStore, unknown, Action> => async (dispatch) => {
  const actionKey = getUserData;

  try {
    dispatch(startLoadingAction(actionKey));
    const {data: userData} = await getUserProfileApi();

    if (userData?.userAvatarId) {
      dispatch(loadMediaAction(userData.userAvatarId));
    }

    if (userData?.dialogAvatarId) {
      dispatch(loadMediaAction(userData.dialogAvatarId));
    }

    if (userData?.language) {
      dispatch(changeLocale(userData.language));
    }

    dispatch(setUserLanguageAction(userData));
  } catch (e) {
    console.error('getUserData error', {
      message: e.message,
      status: e.status,
      response: e.response?.data,
      stack: e.stack
    });
    dispatch(errorHandler({
      toastKey: ToastKey.GetUserData,
      actionKey,
      error: e,
      currentAction: getUserDataAction()
    }));
  } finally {
    dispatch(finishLoadingAction(actionKey));
  }
};

export const updateUserProfileAction = (profile: IUserProfile): ThunkAction<void, IStore, unknown, Action> => async (dispatch) => {
  try {
    const {data} = await updateUserProfileApi(profile);

    dispatch(setUserLanguageAction(data));
  } catch (e) {
    console.error('updateUserProfileAction error', {
      message: e.message,
      status: e.status,
      response: e.response?.data,
      stack: e.stack
    });
    dispatch(errorHandler({
      toastKey: ToastKey.UpdateUserProfileAction,
      actionKey: 'updateUserProfileAction',
      error: e
    }));
  }
};

export const updateUserNickAction = (profile: IUserProfile): ThunkAction<void, IStore, unknown, Action> => async (dispatch) => {
  try {
    const {data} = await updateUserProfileNicknameApi(profile.nickname);

    if (data) {
      dispatch(setUserNicknameAction(profile));
    }
  } catch (e) {
    console.error('setUserNicknameAction error', {
      message: e.message,
      status: e.status,
      response: e.response?.data,
      stack: e.stack
    });
    dispatch(errorHandler({
      toastKey: ToastKey.UpdateUserProfileAction,
      actionKey: 'setUserNicknameAction',
      error: e
    }));
  }
};

export const finishLoginAction = (): ThunkAction<void, IStore, unknown, Action> => async (dispatch, getState) => {
  const {auth: {auth0Client} } = getState();

  try {
    await auth0Client.handleRedirectCallback();
    const user = await auth0Client.getUser() as IUser;
    const token = await auth0Client.getTokenSilentlyWithFallback();

    setAxiosHeaders(user, token);

    dispatch(loginErrorActionClear());
    dispatch(setUserAction(user));
    dispatch(getUserDataAction());
  } catch (e) {
    console.error('finishLogin error', {
      message: e.message,
      error: e.error,
      errorDescription: e.error_description,
      stack: e.stack
    });
    await loginErrorHandler({
      dispatch,
      auth0Client
    });
  }
};

export const logoutAction = (): ThunkAction<void, IStore, unknown, Action> => async (dispatch, getState) => {
  const {auth: {auth0Client} } = getState();

  dispatch(loginErrorActionClear());

  try {
    await auth0Client.logout({
      logoutParams: {
        returnTo: document.location.origin
      }
    });
    dispatch(clearUserAction());
  } catch (e) {
    console.error('logoutAction error', {
      message: e.message,
      error: e.error,
      errorDescription: e.error_description,
      stack: e.stack
    });
    await loginErrorHandler({
      dispatch,
      auth0Client
    });
  }
};

export const getAuthorizationAction = (): ThunkAction<void, IStore, unknown, Action> => async (dispatch, getState) => {
  const {auth: {auth0Client} } = getState();

  dispatch(loginErrorActionClear());

  try {
    const isAuthenticated = await auth0Client.isAuthenticated();

    if (isAuthenticated) {
      const user = await auth0Client.getUser() as IUser;
      const token = await auth0Client.getTokenSilentlyWithFallback();

      setAxiosHeaders(user, token);
      dispatch(setUserAction(user as IUser));
      dispatch(getUserDataAction());
    }
    dispatch(finishAuthorizationAction());
  } catch (e) {
    console.error('getAuthorizationAction error', {
      message: e.message,
      error: e.error,
      errorDescription: e.error_description,
      stack: e.stack
    });
    await loginErrorHandler({
      dispatch,
      auth0Client
    });
  }
};
